import { Editor } from "@tiptap/core";
import { NodeType } from "@tiptap/pm/model";
import { Plugin, PluginKey, PluginView } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

import { useLayoutContainer } from "@/components/base/nw-draggable-panel/hooks";
import { getEditorInstanceId } from "@/editor/utils/editor";

type WaffleMenuState = {
  show: boolean;
  referencePos: DOMRect;
  nodeId: string | undefined;
};

class WaffleMenuView implements PluginView {
  public state?: WaffleMenuState;
  public readonly emitUpdate: (state: WaffleMenuState) => void;

  private mousePos: { x: number; y: number } | undefined;
  private hoveredBlock: HTMLElement | undefined;
  private indicatorElement: HTMLElement;
  private scrollTimeout: number | undefined;
  private layoutContainer = useLayoutContainer();

  constructor(
    private readonly editor: Editor,
    private readonly sideMenuDetection: "viewport" | "editor",
    private readonly pmView: EditorView,
    private readonly nodeTypes: string[],
    private readonly wrapperTags: string[],
  ) {
    this.indicatorElement = this.initIndicator();
    document.body.appendChild(this.indicatorElement);

    this.emitUpdate = () => {
      if (!this.state) {
        throw new Error("Attempting to update uninitialized side menu");
      }

      this.updateIndicator();
    };

    this.pmView.root.addEventListener("mousemove", this.onMouseMove as EventListener, true);
    this.layoutContainer.onRegister(ref => ref.addEventListener("scroll", this.onScroll, { passive: true }));
  }

  initIndicator = () => {
    const element = document.createElement("nw-bookmark-btn");
    element.classList.add("node-hover-bookmark-btn");
    element.style.zIndex = "10";
    element.style.display = "none";
    element.style.opacity = "0";
    element.style.transition = "opacity 0.1s ease-in-out";
    return element;
  };

  updateIndicator = () => {
    if (this.state?.show) {
      this.indicatorElement.setAttribute("node-id", this.state.nodeId!);
      this.indicatorElement.setAttribute("editor-instance-id", getEditorInstanceId(this.editor));
      this.indicatorElement.style.display = "block";

      const nodeElement = this.pmView.dom.querySelector(`[node-id="${this.state.nodeId}"]`);
      let leftPosition = this.state.referencePos.x - this.indicatorElement.getBoundingClientRect().width;

      if (nodeElement && (nodeElement.closest("li") || nodeElement.closest("ul") || nodeElement.closest("ol"))) {
        leftPosition -= 16;
      }

      this.indicatorElement.style.left = `${leftPosition}px`;
      this.indicatorElement.style.top = `${this.state.referencePos.y - 4}px`;

      this.indicatorElement.offsetHeight;
      this.indicatorElement.style.opacity = "1";
    } else {
      this.indicatorElement.style.opacity = "0";

      setTimeout(() => {
        if (this.indicatorElement.style.opacity === "0") {
          this.indicatorElement.style.display = "none";
        }
      }, 100);
    }
  };

  updateState = (state: WaffleMenuState) => {
    this.state = state;
    this.emitUpdate(this.state);
  };

  updateStateFromMousePos = () => {
    if (!this.mousePos) {
      return;
    }

    const editorRoot = this.pmView.dom!;

    const block = this.getBlockFromMousePos(this.mousePos, this.pmView, this.sideMenuDetection);
    const isEditorRoot = block?.node === editorRoot;
    const isBookmarked =
      block?.node.getAttribute("bookmark") == "true" && block?.node.classList.contains("bookmarked-node");
    if (!block || isEditorRoot || isBookmarked) {
      this.hideMenu();
      return;
    }

    if (this.hoveredBlock && this.hoveredBlock.contains(block.node) && this.hoveredBlock !== block.node) {
      return;
    }

    const pos = this.pmView.posAtDOM(block.node, 0);
    if (!pos) {
      this.hideMenu();
      return;
    }

    const $pos = this.pmView.state.doc.resolve(pos);
    const parentType = $pos.parent.type;
    const node = $pos.node($pos.depth);

    // Don't show on empty nodes
    if (node.content.size === 0 || (block.node.innerText && block.node.innerText.trim() === "")) {
      this.hideMenu();
      return;
    }

    // The following is to filter out nodes that are not observed
    if (!this.isObservedNodeType(parentType)) {
      this.hideMenu();
      return;
    }

    // Skip text nodes
    if (parentType.name === "text") {
      return;
    }

    // Skip inline nodes
    if (parentType.isInline || $pos.node().type.isInline) {
      return;
    }

    // Doesn't update if the menu is already open and the mouse cursor is still hovering the same block.
    if (
      this.state?.show &&
      this.hoveredBlock?.hasAttribute("node-id") &&
      this.hoveredBlock?.getAttribute("node-id") === block.id
    ) {
      return;
    }

    this.hoveredBlock = block.node;

    const blockContent = block.node;

    if (!blockContent) {
      return;
    }

    const blockContentBoundingBox = blockContent.getBoundingClientRect();
    this.updateState({
      show: true,
      referencePos: new DOMRect(
        blockContentBoundingBox.x,
        blockContentBoundingBox.y,
        blockContentBoundingBox.width,
        blockContentBoundingBox.height,
      ),
      nodeId: this.hoveredBlock!.getAttribute("node-id")!,
    });
  };

  hideMenu = () => {
    if (this.state?.show) {
      this.state.show = false;
      this.updateState(this.state);
    }
  };

  onMouseMove = (event: MouseEvent) => {
    this.mousePos = { x: event.clientX, y: event.clientY };

    const editorOuterBoundingBox = this.pmView.dom.getBoundingClientRect();
    const cursorWithinEditor = this.isWithinEditor(this.mousePos, editorOuterBoundingBox);

    const editorWrapper = this.pmView.dom!.parentElement;
    const target = event.target as HTMLElement;
    const isElementWithinEditor = editorWrapper === target || editorWrapper?.contains(target);

    if (cursorWithinEditor && target && !isElementWithinEditor) {
      if (this.state?.show) {
        this.state.show = false;
        this.emitUpdate(this.state);
      }

      return;
    }

    this.updateStateFromMousePos();
  };

  isObservedNodeType = (node: NodeType | string) => {
    if (typeof node === "string") {
      return this.nodeTypes.length > 0 && this.nodeTypes.includes(node);
    }

    return this.nodeTypes.length > 0 && this.nodeTypes.includes(node.name);
  };

  isWithinEditor = (mousePos: { x: number; y: number }, editorOuterBoundingBox: DOMRect) => {
    return (
      mousePos.x > editorOuterBoundingBox.left - 100 &&
      mousePos.x < editorOuterBoundingBox.right &&
      mousePos.y > editorOuterBoundingBox.top &&
      mousePos.y < editorOuterBoundingBox.bottom
    );
  };

  onScroll = () => {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.state?.show) {
      this.state.show = false;
      this.state.referencePos = this.hoveredBlock!.getBoundingClientRect();
      this.emitUpdate(this.state);
    }
  };

  destroy() {
    if (this.state?.show) {
      this.state.show = false;
      this.emitUpdate(this.state);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.pmView.root.removeEventListener("mousemove", this.onMouseMove as EventListener, true);
    this.layoutContainer.ref.value?.removeEventListener("scroll", this.onScroll);
    this.indicatorElement.remove();
  }

  private getBlockFromCoords(
    view: EditorView,
    coords: { left: number; top: number },
    sideMenuDetection: "viewport" | "editor",
  ) {
    const elements = view.root.elementsFromPoint(coords.left + (sideMenuDetection === "editor" ? 50 : 0), coords.top);
    for (const element of elements) {
      if (!view.dom.contains(element) || this.isMarkOrInsideMark(element as HTMLElement)) {
        // probably a ui overlay
        continue;
      }

      return { node: element as HTMLElement, id: element.getAttribute("node-id")! };
    }
    return undefined;
  }

  private getBlockFromMousePos(
    mousePos: {
      x: number;
      y: number;
    },
    view: EditorView,
    sideMenuDetection: "viewport" | "editor",
  ): { node: HTMLElement; id: string } | undefined {
    // Editor itself might have other styling which affects size/position, so we get the boundingRect of the first child that wraps all blocks in the editor) for more accurate menu placement.
    if (!view.dom.firstChild) {
      return;
    }

    const editorBoundingBox = (view.dom.firstChild as HTMLElement).getBoundingClientRect();
    const coords = {
      left: mousePos.x,
      top: mousePos.y,
    };

    const mouseLeftOfEditor = coords.left < editorBoundingBox.left;
    const mouseRightOfEditor = coords.left > editorBoundingBox.right;

    // Clamps the x position to the editor's bounding box.
    if (sideMenuDetection === "viewport") {
      if (mouseLeftOfEditor) {
        coords.left = editorBoundingBox.left + 10;
      }

      if (mouseRightOfEditor) {
        coords.left = editorBoundingBox.right - 10;
      }
    }

    let block = this.getBlockFromCoords(view, coords, sideMenuDetection);

    // If we didn't find a block directly under the cursor, check if we're within 100px of the left edge
    if (!block) {
      coords.left = mousePos.x + 100;
      block = this.getBlockFromCoords(view, coords, sideMenuDetection);

      if (block) {
        const blockRect = block.node.getBoundingClientRect();
        if (mousePos.x < blockRect.left + 100) {
          return block;
        }
      }
    }

    if (!mouseRightOfEditor && block) {
      /* Because blocks can be nested
        | BlockA        |
        x | BlockB     y|
    
        hovering over position x (the "margin of block B") will return block A instead of block B.
        to fix this, we get the block from the right side of block A (position y, which will fall in BlockB correctly)
        */
      const rect = block.node.getBoundingClientRect();
      coords.left = rect.right - 10;
      block = this.getBlockFromCoords(view, coords, "viewport");
    }

    // If target is a mark or inside a mark, find the nearest non-mark parent
    if (block && this.isMarkOrInsideMark(block.node)) {
      const nonMarkParent = this.findNearestNonMarkParent(block.node);

      if (!nonMarkParent) {
        return;
      }

      block.node = nonMarkParent;
      block.id = nonMarkParent.getAttribute("node-id")!;
    }

    return block;
  }

  /**
   * Checks if an element is a mark or is inside a mark element.
   * This includes various text formatting marks like bold, italic, underline, etc.
   *
   * @param element - The DOM element to check
   * @returns true if the element is a mark or is inside a mark, false otherwise
   * @param element
   * @returns
   */
  private isMarkOrInsideMark(element: HTMLElement): boolean {
    if (element.classList.contains("bookmarked-node") && element.getAttribute("bookmark") == "true") {
      return false;
    }

    const markClasses = [
      "bold",
      "italic",
      "underline",
      "strike",
      "highlight",
      "link",
      "code",
      "ProseMirror-mark",
      "nw-highlight",
    ];

    const markTags = ["strong", "b", "em", "i", "u", "s", "strike", "mark", "a", "code", "nw-highlight"];

    let current: HTMLElement | null = element;
    while (current) {
      // Check if the element is inside a wrapper element (but not the wrapper element itself)
      if (this.wrapperTags.includes(current.tagName.toLowerCase()) && current !== element) {
        return true;
      }

      if (
        current.getAttribute("class")?.includes("mark") ||
        markClasses.some(cls => current?.classList.contains(cls))
      ) {
        return true;
      }

      const tagName = current.tagName.toLowerCase();
      if (markTags.includes(tagName)) {
        return true;
      }

      // Specific condition for inline code blocks to treat them as marks but not their block variant
      if (tagName === "nw-code-block" && current.getAttribute("inline") == "true") {
        return true;
      }

      current = current.parentElement;
    }

    return false;
  }

  /**
   * Finds the nearest parent element that is not a mark
   *
   * @param element - The DOM element to start from
   * @returns The nearest non-mark parent element, or null if not found
   */
  private findNearestNonMarkParent(element: HTMLElement): HTMLElement | null {
    let current = element.parentElement;

    while (current) {
      const isMarkElement = this.isMarkOrInsideMark(current);

      if (!isMarkElement) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }
}

export const sideMenuPluginKey = new PluginKey("SideMenuPlugin");

export class WaffleMenuPlugin {
  public view: WaffleMenuView | undefined;
  public readonly plugin: Plugin;

  constructor(editor: Editor, nodeTypes: string[], wrapperTags: string[]) {
    this.plugin = new Plugin({
      key: sideMenuPluginKey,
      view: editorView => {
        this.view = new WaffleMenuView(editor, "viewport", editorView, nodeTypes, wrapperTags);
        return this.view;
      },
    });
  }
}
